<template>
	<tbody class="data-table-body-wrapper">
		<component
		v-for="(rowData, index) in tableData"
		:is="rowComponent"
		:rowData="rowData"
		:configurationData="configurationData"
		:key="index"
        :tableIdentifier="tableIdentifier"
		:rowIndex="index"
		:rowHeightClass="rowHeightClass"
		:disabled="disabled"
		:tableStyles="tableStyles"
        :hasDetail="hasDetail"
		:hasRowClick="hasDetail && !showOptionsOpened"
		:alwaysBehaveLikeMobile="alwaysBehaveLikeMobile"
		@rowClicked="$emit('rowClicked', $event)"
		@closeRowClicked="emit('closeRowClicked', $event)"
		@moreOptionsClicked="$emit('moreOptionsClicked', $event)"
		@selectedRadio="emit('selectedRadio', $event)"
		@toggleSwitch="emit('toggleSwitch', $event)"
		@dropdownClicked="emit('dropdownClicked', $event)"
		@onBlur="emit('onBlur', $event)"
		@inputChanged="emit('inputChanged', $event)"
		@downloadClicked="emit('downloadClicked', $event)"
		@buttonClicked="emit('buttonClicked', $event)"/>
	</tbody>
</template>

<script setup>
/* Vue */
import { ref, computed, defineAsyncComponent } from 'vue'

/* Emmitters */
const emit = defineEmits(['rowClicked', 'closeRowClicked', 'moreOptionsClicked', 'selectedRadio', 'toggleSwitch', 'dropdownClicked', 'inputChanged', 'onBlur', 'downloadClicked', 'buttonClicked'])

/** Props received:
@tableIdentifier {String} - prop to help for dropdown teleports when we have multiple tables in same feature
@rowType { String } - prop to define row type [generic(default)]
@rowHeightClass { String } - prop to apply class to row, to control it's height: [default height(76px), medium(96px)]
@configurationData { Array } - each Object is a column in your list:
	dataAttr[String, Number]: identifier, used for sortable and to create groups, use (when available) prop provided by MDW for this column
	showInMobile [Boolean, Object]:
		[Boolean] if we receive a boolean your cell in mobile will be a column
		[Object] if we receive an object, your cell will have multiple lines in one column
			key: groupWith, value: dataAttr we want to pair with this cell in mobile layout
            key: groupInline, value: boolean to let us know if the cells we want to group here will be in a single line below main cell
@tableData {Array}
@disabled {Boolean} - prop to disabled clicks on table
@hasDetail { Boolean } - prop to add hover styles and icon at the right that shows that row has detail
@tableStyles {Array}
@alwaysBehaveLikeMobile {Boolean} - prop to force mobile layout even in higher resolutions
*/
const props = defineProps({
    tableIdentifier: String,
	rowType: String,
	rowHeightClass: String,
	configurationData: Array,
	tableData: Array,
	disabled: Boolean,
	hasDetail: Boolean,
	tableStyles: Array,
	alwaysBehaveLikeMobile: Boolean // prop to force mobile layout even in higher resolutions
})

const showOptionsOpened = ref(false)
/* Dynamic Row Component */
const rowComponent = computed(() => {
	return defineAsyncComponent(() => import('@/components/generic/table/rows/' + rowsComponentMapping[props.rowType]))
})

/** maps the list of components that are used as cell */
const rowsComponentMapping = {
	generic: 'Generic'
}
</script>

<style lang="scss" scoped>
.data-table-body-wrapper {
    width: 100%;
}
</style>
